import React, { ReactNode, useContext, useEffect } from 'react'
import Link from 'next/link'
import classNames from 'classnames'
import { Toaster } from 'react-hot-toast'
import { WorkWithParsedSrts } from '@src/pages/api/work/[id]'
import { ConfigContext } from '@src/context/ConfigContext'
import { useConfig } from '@src/lib/apiClient'
import { Exhibition } from '@prisma/client'
import { GuestHead } from '@src/components/GuestHead'
import { DEFAULT_INSTRUCTIONS } from '@src/components/ConfigForm'
import { ArrowLeft, AllIconsPreloader } from './icons'
import Loader from '../public/images/loader.svg'

type LayoutProps = {
  work?: WorkWithParsedSrts
  workIsLoading?: boolean;
  exhibition?: Exhibition;
  exhibitionIsLoading?: boolean;
  focusMode?: boolean;
  children: ReactNode;
};

const Layout: React.FC<LayoutProps> = ({
  work,
  workIsLoading,
  children,
  exhibition,
  exhibitionIsLoading,
  focusMode = false,
}) => {
  const { setConfigContext } = useContext(ConfigContext)
  const { data: config } = useConfig()
  useEffect(() => {
    setConfigContext(config)
  }, [config, setConfigContext])
  let navLink = config?.indexBacklinkUrl ?? '/'
  let navText = config?.indexBacklinkText ?? 'Mobile Guide'
  let headline = config?.title ?? 'Captions and Audio'
  let instructions = null
  // TODO: lets just be explicit here and pass the pageType to the layout or determine it from the route instead of this
  if (work || workIsLoading) {
    // is work page
    navText = 'Back'
    navLink = exhibition ? `/exhibition/${exhibition.id}` : '/'
    headline = null
  } else if (exhibition || exhibitionIsLoading) {
    // is exhibition page
    headline = exhibition ? exhibition.name : ''
    navText = 'Back'
    navLink = '/'
  } else {
    // is homepage
    instructions = config?.instructions ?? DEFAULT_INSTRUCTIONS
  }

  const MiniHeader = () => (
    <>
      <div className="mini-header">
        <Link href={navLink}>
          <ArrowLeft />
          {navText}
        </Link>
      </div>
      {headline && <h1 className="headline">{headline}</h1>}
      {instructions && <p className="descriptive-header">{instructions}</p>}
    </>
  )

  return (
    <div id="root">
      <div className="custom-style-loader">
        <Loader />
      </div>
      <GuestHead config={config} work={work} exhibition={exhibition} />
      <AllIconsPreloader />
      <div className={classNames({ layout: true, 'layout__sticky-footer': true })}>
        {
          !focusMode && (
            <MiniHeader />
          )
        }

        <main style={focusMode ? { height: '100%' } : { flex: 1 }}>{children}</main>

        {!focusMode && <footer>{`© ${(new Date()).getFullYear()} ${config?.organizationName ?? ''}`}</footer>}
        <Toaster />
      </div>
    </div>
  )
}

export default Layout
